import { z } from 'zod';

import { DISTRIBUTION_TERRITORIES } from '~settings/api/lib/types';
import { DEAL_REGION_SCOPE } from '~settings/constants';

export const BaseDistributionGroupSchema = z.object({
  id: z.string().optional(),
  name: z.string().trim().min(1).max(200),
  notes: z.string().trim().max(500).nullable().optional(),
});

const WorldwideDistributionGroup = BaseDistributionGroupSchema.extend({
  type: z.literal(DISTRIBUTION_TERRITORIES.Worldwide),
  distributionGroupCountries: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      }),
    )
    .prefault([]),
});

const SelectedDistributionGroup = BaseDistributionGroupSchema.extend({
  type: z.enum([
    DISTRIBUTION_TERRITORIES.OnlyIn,
    DISTRIBUTION_TERRITORIES.WorldwideExclude,
  ]),
  distributionGroupCountries: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      }),
    )
    // todo PERMS-1083: make a helper function to translate custom message
    // as they don't trigger getI18nErrorMap
    .min(1, {
      error: 'Countries are required when the distribution is not Worldwide.',
    }),
});

export const DistributionGroupSchema = z.discriminatedUnion('type', [
  WorldwideDistributionGroup,
  SelectedDistributionGroup,
]);

export type DistributionGroup = z.infer<typeof DistributionGroupSchema>;

export const DistributionGroupListSchema = z.object({
  data: z.array(DistributionGroupSchema),
});
export type DistributionGroupList = z.infer<typeof DistributionGroupListSchema>;

export const labelFormSchema = z.object({
  active: z.boolean().optional(),
  labelName: z.string().trim().min(1).max(200),
  labelGroups: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      }),
    )
    .optional(),
  weaLabelCode: z.string().trim().min(1).max(200),
  usLabelCode: z.string().trim().min(1).max(200),

  dealOwner: z.string().trim().min(1),
  dealRegionScope: z.enum(DEAL_REGION_SCOPE),
  wmiLabelCode: z.string().trim().min(1),
  marketingCompany: z.string().trim().min(1),
  repertoireOwner: z.string().trim().min(1),
  wbsElementNumber: z.string().trim().min(1),

  // todo PERMS-1083: make a helper function to translate custom message
  // as they don't trigger getI18nErrorMap
  dealId: z
    .string()
    .trim()
    .regex(/^\d{8}$/, {
      error: 'Should contain 8 digits only',
    }),

  // todo PERMS-1083: make a helper function to translate custom message
  // as they don't trigger getI18nErrorMap
  scopeId: z
    .string()
    .trim()
    .regex(/^\d{3}$/, {
      error: 'Should contain 3 digits only',
    }),
  dealScope: z.string().optional(),
  profitCenter: z.string().trim().min(1),
  pmo: z.string().trim().min(1),
  gvlCode: z.string().trim().min(1).max(5),
  labelPresentation: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .superRefine((val, ctx) => {
      if (!val.value.trim().length) {
        ctx.addIssue({
          code: 'too_small',
          minimum: 1,
          type: 'string',
          inclusive: true,
          origin: 'string',
        });
      }
    }),
  distributorId: z.string().trim().min(1),
  gpiNumber: z.string().trim().min(1),

  distributionGroups: z.array(DistributionGroupSchema),
  facebookReference: z.string().nullable(),
  twitterReference: z.string().nullable(),
  youtubeReference: z.string().nullable(),
  instagramReference: z.string().nullable(),
  website: z.string().nullable(),

  allowTimedRelease: z.boolean().prefault(false),
  asylum: z.boolean().prefault(false),
  directPassQc: z.boolean().prefault(false),
  parentLabel: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .optional(),
  labelIsrcPrefix: z.string().nullable(),
});

export type LabelFormSchema = z.infer<typeof labelFormSchema>;
